import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Icon,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import Loader from "@jumbo/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { getMeeshoMarkingExpensesList } from "app/redux/slice/meeshoOrderManagementSlice";
import { Table as CustomTable } from "app/components";
import moment from "moment";
import { useSnackbar } from "notistack";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const MarkingExpensesList = () => {
  const dispatch = useDispatch();
  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const orders = meeshoData?.markingExpenses?.list || [];
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-management-marking-expenses" });

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, searchTerm, orderBy]);

  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const fetchOrders = async () => {
    const params = {
      page: page,
      perPage: rowsPerPage,
      search: searchTerm?.trim(),
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }

    if (dateRange[0] !== null || dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getMeeshoMarkingExpensesList(params));
  };

  const handleApplyFilter = () => {
    const params = {
      page: page,
      perPage: rowsPerPage,
      search: searchTerm?.trim(),
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }

    params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    dispatch(getMeeshoMarkingExpensesList(params));
  };

  const columns = [
    {
      id: "campaignID",
      label: "Campaign ID",
      sort: true,
    },
    {
      id: "deductionDate",
      label: "Deduction Date",
      sort: true,
      render: (row) => (
        <>{moment.utc(row?.deductionDate).format("DD/MM/YYYY")}</>
      ),
    },
    {
      id: "deductionDuration",
      label: "Deduction Duration Date",
      sort: true,
      render: (row) => (
        <>{moment.utc(row?.deductionDuration).format("DD/MM/YYYY")}</>
      ),
    },
    {
      id: "adsCost",
      label: "Ads Cost",
      sort: true,
      align: "right",
      isTotal: true,
      format: (value) => formatCurrency(value),
    },
    {
      id: "discount",
      label: "Discount",
      sort: true,
      align: "right",
      isTotal: true,
      format: (value) => formatCurrency(value),
    },
    {
      id: "gst",
      label: "GST",
      sort: true,
      align: "right",
      isTotal: true,
      format: (value) => formatCurrency(value),
    },
    {
      id: "subTotal",
      label: "Sub Total",
      sort: true,
      align: "right",
      isTotal: true,
      format: (value) => formatCurrency(value),
    },
    {
      id: "total",
      label: "Total",
      sort: true,
      align: "right",
      isTotal: true,
      format: (value) => formatCurrency(value),
    },
  ];

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    handleChangePage(0, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Marketing And Expenses List
      </Typography>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date - To Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={handleDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={meeshoData?.statusList?.loading}
                startIcon={
                  meeshoData?.statusList?.loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    ""
                  )
                }
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm("");
                  handleChangePage(0, 0);
                  setDateRange([null, null]);
                  setOrderBy("createdAt");
                  setOrder("desc");
                  fetchOrders();
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {meeshoData?.markingExpenses?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.markingExpenses?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.markingExpenses?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MarkingExpensesList;
